import React, { useMemo, useState } from 'react';
import { Building2, ExternalLink, Globe, LayoutDashboard, Link2, RefreshCw, ShieldCheck, Users } from 'lucide-react';

const MetricItem = ({ label, value }) => (
  <div className="bg-card rounded-xl border border-border-subtle p-4">
    <p className="text-text-muted text-xs uppercase tracking-wider mb-1">{label}</p>
    <p className="text-text-heading font-heading text-xl font-semibold">{value}</p>
  </div>
);

export const BusinessLinesHub = ({ data, onRefresh }) => {
  const [selectedModuleId, setSelectedModuleId] = useState('dashboard');

  const selectedModule = useMemo(
    () => data?.internal_business?.modules?.find((module) => module.id === selectedModuleId) || data?.internal_business?.modules?.[0],
    [data, selectedModuleId]
  );

  if (!data) return null;

  const solutions = data.solutions_business || {};
  const partner = data.partner_business || {};

  return (
    <div className="space-y-6" data-testid="business-lines-hub">
      <div className="glass-card tracing-panel p-6 xl:p-8" data-testid="business-lines-ecosystem-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-text-heading font-heading text-2xl font-semibold">Dos líneas de negocio, un solo ecosistema</h3>
            <p className="text-text-body text-sm mt-2 max-w-4xl">NEURALIA369 queda ahora claramente separada entre <strong>Neuralia Solutions</strong> para empresas y emprendedores, y <strong>Neuralia Partner</strong> para red comercial, afiliación inteligente y agencias. Todo vive dentro del mismo ecosistema, apoyado por la capa operativa de Neuralia OS.</p>
          </div>
          <button onClick={onRefresh} className="p-2 rounded-lg bg-card border border-border-subtle text-text-muted hover:text-primary transition-all" data-testid="refresh-business-lines">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <div className="glass-card p-6" data-testid="solutions-business-card">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center"><Globe className="w-6 h-6 text-primary" /></div>
              <div>
                <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Línea 1</p>
                <h4 className="text-text-heading font-heading font-semibold text-xl">{solutions.label || 'Neuralia Solutions'}</h4>
                <p className="text-text-body text-sm mt-1">{solutions.positioning}</p>
                <p className="text-text-muted text-xs mt-1 break-all">{solutions.base_url}</p>
              </div>
            </div>
            <a href={solutions.base_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm py-2 px-4" data-testid="open-solutions-business">
              <ExternalLink className="w-4 h-4" /> Abrir
            </a>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <MetricItem label="Servicios" value={solutions.metrics?.packages || 0} />
            <MetricItem label="Leads" value={solutions.metrics?.leads || 0} />
            <MetricItem label="Pagos" value={solutions.metrics?.paid_transactions || 0} />
            <MetricItem label="Contratos" value={solutions.metrics?.contracts || 0} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(solutions.access_routes || []).map((route) => (
              <a key={route.path} href={`${(solutions.base_url || '').replace(/\/$/, '')}${route.path}`} target="_blank" rel="noopener noreferrer"
                className="bg-card rounded-xl border border-border-subtle p-4 text-text-body hover:border-primary/40 transition-all" data-testid={`solutions-route-${route.path.replace(/[^a-z0-9]/gi, '-')}`}>
                <p className="text-text-heading font-medium">{route.label}</p>
                <p className="text-text-muted text-xs mt-1">{route.path}</p>
              </a>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="partner-business-card">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/15 flex items-center justify-center"><Users className="w-6 h-6 text-cyan-300" /></div>
              <div>
                <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Línea 2</p>
                <h4 className="text-text-heading font-heading font-semibold text-xl">{partner.label || 'Neuralia Partner'}</h4>
                <p className="text-text-body text-sm mt-1">{partner.positioning}</p>
                <p className="text-text-muted text-xs mt-1 break-all">{partner.base_url}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <MetricItem label="Partners" value={partner.metrics?.partners || 0} />
            <MetricItem label="Agencias" value={partner.metrics?.agencias || 0} />
            <MetricItem label="Leads red" value={partner.metrics?.partner_leads || 0} />
            <MetricItem label="Payouts" value={partner.metrics?.pending_payouts || 0} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(partner.access_routes || []).map((route) => (
              <a key={route.path} href={`${(partner.base_url || '').replace(/\/$/, '')}${route.path}`} target="_blank" rel="noopener noreferrer"
                className="bg-card rounded-xl border border-border-subtle p-4 text-text-body hover:border-primary/40 transition-all" data-testid={`partner-route-${route.path.replace(/[^a-z0-9]/gi, '-')}`}>
                <p className="text-text-heading font-medium">{route.label}</p>
                <p className="text-text-muted text-xs mt-1">{route.path}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="internal-business-card">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 flex items-center justify-center"><Building2 className="w-6 h-6 text-indigo-300" /></div>
            <div>
              <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Capa ecosistema</p>
              <h4 className="text-text-heading font-heading font-semibold text-xl">Neuralia OS / Director IA</h4>
              <p className="text-text-body text-sm mt-1">Motor operativo compartido por Solutions y Partner.</p>
              <p className="text-text-muted text-xs mt-1 break-all">{data.internal_business.base_url}</p>
            </div>
          </div>
          <span className={`text-xs px-3 py-1 rounded-full ${data.internal_business.connected ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`} data-testid="internal-business-status">
            {data.internal_business.connected ? 'Conectado' : 'Revisar conexión'}
          </span>
        </div>

        {data.internal_business.connected ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <MetricItem label="Usuarios" value={data.internal_business.metrics?.users?.total || 0} />
              <MetricItem label="Facturas pagadas" value={data.internal_business.metrics?.invoices?.paid || 0} />
              <MetricItem label="Ingresos" value={data.internal_business.metrics?.revenue?.total || 0} />
              <MetricItem label="Tareas" value={data.internal_business.metrics?.tasks?.total || 0} />
            </div>
            <div className="bg-card rounded-xl border border-border-subtle p-4 mb-5">
              <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Usuario conectado en la integración</p>
              <p className="text-text-heading font-medium">{data.internal_business.auth_user?.name} · {data.internal_business.auth_user?.email}</p>
              <p className="text-text-muted text-sm mt-1">Esta capa no es una línea comercial distinta: es la operación central del ecosistema.</p>
            </div>
          </>
        ) : (
          <div className="bg-card rounded-xl border border-border-subtle p-4 mb-5 text-yellow-300 text-sm" data-testid="internal-business-error">
            {data.internal_business.error || 'No se ha podido conectar con el sistema interno.'}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          {data.internal_business.modules?.map((module) => (
            <button key={module.id} type="button"
              onClick={() => setSelectedModuleId(module.id)}
              className={`text-left rounded-xl border p-4 transition-all ${selectedModule?.id === module.id ? 'border-primary bg-primary/5' : 'border-border-subtle bg-card hover:border-primary/30'}`}
              data-testid={`internal-module-${module.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-text-heading font-medium">{module.label}</p>
                {module.requires_auth ? <ShieldCheck className="w-4 h-4 text-text-muted" /> : <Link2 className="w-4 h-4 text-text-muted" />}
              </div>
              <p className="text-text-muted text-xs mb-2">{module.path}</p>
              <p className="text-text-body text-sm">{module.description}</p>
            </button>
          ))}
        </div>
      </div>

      {selectedModule && (
        <div className="glass-card p-6" data-testid="internal-embed-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Módulo interno</p>
              <h4 className="text-text-heading font-heading font-semibold text-xl">{selectedModule.label}</h4>
              <p className="text-text-body text-sm mt-1">{selectedModule.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <a href={`${data.internal_business.base_url}/auth`} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm py-2 px-4" data-testid="open-internal-login">
                <ShieldCheck className="w-4 h-4" /> Login interno
              </a>
              <a href={selectedModule.full_url} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm py-2 px-4" data-testid="open-selected-module">
                <ExternalLink className="w-4 h-4" /> Abrir módulo
              </a>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border-subtle overflow-hidden" style={{ minHeight: 720 }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border-subtle text-text-muted text-xs">
              <LayoutDashboard className="w-4 h-4" />
              {selectedModule.full_url}
            </div>
            <iframe title={selectedModule.label} src={selectedModule.full_url} className="w-full border-0" style={{ minHeight: 680 }} data-testid="internal-module-iframe" />
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessLinesHub;